// RichText.js

export const RICH_TEXT_KIND = 'tiptap';
export const RICH_TEXT_VERSION = 1;
export const RICH_TEXT_EMBEDDED_NODE_TYPE = 'embeddedNode';

const BLOCK_NODE_TYPES = new Set([
    'blockquote',
    'bulletList',
    'codeBlock',
    RICH_TEXT_EMBEDDED_NODE_TYPE,
    'heading',
    'listItem',
    'orderedList',
    'paragraph',
    'taskItem',
]);

export const createEmptyRichTextDocument = () => ({
    type: 'doc',
    content: [{ type: 'paragraph' }],
});

const createPlainTextDocument = (value) => {
    const text = String(value || '');
    const trimmed = text.trim();

    if (!trimmed) return createEmptyRichTextDocument();

    return {
        type: 'doc',
        content: [
            {
                type: 'paragraph',
                content: [{ type: 'text', text: trimmed }],
            },
        ],
    };
};

const normalizePlainText = (value) => {
    return String(value || '')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .replace(/[ \t]{2,}/g, ' ')
        .trim();
};

const buildPlainTextFromNode = (node) => {
    if (!node) return '';

    if (Array.isArray(node)) {
        return node.map((entry) => buildPlainTextFromNode(entry)).join('');
    }

    if (typeof node !== 'object') return '';

    if (node.type === 'text') {
        return typeof node.text === 'string' ? node.text : '';
    }

    if (node.type === 'hardBreak') {
        return '\n';
    }

    if (node.type === RICH_TEXT_EMBEDDED_NODE_TYPE) {
        const title = String(node?.attrs?.title || node?.attrs?.nodeType || 'Embedded item').trim();
        return `${title}\n`;
    }

    const contentText = buildPlainTextFromNode(Array.isArray(node.content) ? node.content : []);
    if (BLOCK_NODE_TYPES.has(node.type)) {
        return `${contentText}\n`;
    }

    return contentText;
};

export const extractRichTextPlainText = (value) => {
    const normalized = normalizeRichTextValue(value);
    return normalizePlainText(buildPlainTextFromNode(normalized.content));
};

export const buildRichTextSummary = (value, maxLength = 220) => {
    const text = extractRichTextPlainText(value);
    const normalizedMaxLength = Number.isFinite(Number(maxLength))
        ? Math.max(1, Math.round(Number(maxLength)))
        : 220;

    if (!text || text.length <= normalizedMaxLength) {
        return text;
    }

    const softLimit = Math.max(1, normalizedMaxLength - 3);
    const wordBoundaryIndex = text.lastIndexOf(' ', softLimit);
    const cutoffIndex = wordBoundaryIndex >= Math.floor(softLimit * 0.6)
        ? wordBoundaryIndex
        : softLimit;

    return `${text.slice(0, cutoffIndex).trimEnd()}...`;
};

export const normalizeRichTextDocument = (value) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        if (value.type === 'doc') {
            return value;
        }

        if (Array.isArray(value.content)) {
            return {
                type: 'doc',
                content: value.content,
            };
        }
    }

    if (typeof value === 'string') {
        return createPlainTextDocument(value);
    }

    return createEmptyRichTextDocument();
};

const hasMeaningfulRichTextNodeContent = (node) => {
    if (!node || typeof node !== 'object') return false;

    if (node.type === 'text') {
        return Boolean(String(node.text || '').trim());
    }

    if (node.type === RICH_TEXT_EMBEDDED_NODE_TYPE) {
        return true;
    }

    if (node.type === 'hardBreak') {
        return false;
    }

    const childNodes = Array.isArray(node.content) ? node.content : [];
    return childNodes.some((childNode) => hasMeaningfulRichTextNodeContent(childNode));
};

export const trimTrailingEmptyRichTextBlocks = (value) => {
    const normalizedDocument = normalizeRichTextDocument(value);
    const nextContent = Array.isArray(normalizedDocument.content)
        ? [...normalizedDocument.content]
        : [];

    while (nextContent.length > 0 && !hasMeaningfulRichTextNodeContent(nextContent[nextContent.length - 1])) {
        nextContent.pop();
    }

    if (!nextContent.length) {
        return createEmptyRichTextDocument();
    }

    return {
        ...normalizedDocument,
        content: nextContent,
    };
};

export const createRichTextValue = (content) => {
    const normalizedContent = normalizeRichTextDocument(content);
    const text = normalizePlainText(buildPlainTextFromNode(normalizedContent));

    return {
        kind: RICH_TEXT_KIND,
        version: RICH_TEXT_VERSION,
        content: normalizedContent,
        text,
    };
};

export const normalizeRichTextValue = (value) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        const normalizedContent = normalizeRichTextDocument(value.content ?? value.doc ?? value);
        const derivedText = normalizePlainText(buildPlainTextFromNode(normalizedContent));

        return {
            kind: RICH_TEXT_KIND,
            version: Number.isFinite(Number(value.version)) ? Number(value.version) : RICH_TEXT_VERSION,
            content: normalizedContent,
            text: normalizePlainText(value.text || derivedText),
        };
    }

    if (typeof value === 'string') {
        return createRichTextValue(createPlainTextDocument(value));
    }

    return createRichTextValue(createEmptyRichTextDocument());
};

const canonicalizeRichTextValue = (value) => {
    if (Array.isArray(value)) {
        return value.map((entry) => canonicalizeRichTextValue(entry));
    }

    if (!value || typeof value !== 'object') {
        return value;
    }

    return Object.keys(value)
        .sort((left, right) => left.localeCompare(right))
        .reduce((next, key) => {
            next[key] = canonicalizeRichTextValue(value[key]);
            return next;
        }, {});
};

export const serializeRichTextDocument = (value) => {
    const normalizedDocument = normalizeRichTextDocument(value);
    return JSON.stringify(canonicalizeRichTextValue(normalizedDocument));
};

export const buildEmbeddedNodeAttrs = (node) => {
    const nodeKey = String(node?.nodeKey || node?.key || '').trim();
    const nodeType = String(node?.nodeType || node?.type || 'NODE').trim() || 'NODE';
    const title = String(node?.title || '').trim() || 'Untitled';

    return {
        nodeKey,
        nodeType,
        title,
        description: String(node?.description || '').trim(),
        coverStoragePath: String(node?.coverStoragePath || '').trim(),
        coverUrl: String(node?.coverUrl || '').trim(),
        display: 'card',
        workspaceKey: String(node?.workspaceKey || '').trim(),
    };
};

export const createEmbeddedNodeBlock = (node) => ({
    type: RICH_TEXT_EMBEDDED_NODE_TYPE,
    attrs: buildEmbeddedNodeAttrs(node),
});